import type { Rarity } from "@/api/types";

export interface RarityInfo {
  rarity: Rarity;
  label: string;
  /** Filled cells out of five on the rarity gauge. */
  stars: number;
  /** Order for sorting, rarest first. */
  rank: number;
  blurb: string;
}

export const RARITIES: readonly RarityInfo[] = [
  {
    rarity: "legendary",
    label: "Legendary",
    stars: 5,
    rank: 0,
    blurb: "Seen once, or never before",
  },
  {
    rarity: "epic",
    label: "Epic",
    stars: 4,
    rank: 1,
    blurb: "Two or three sightings on record",
  },
  {
    rarity: "rare",
    label: "Rare",
    stars: 3,
    rank: 2,
    blurb: "Four to nine sightings",
  },
  {
    rarity: "uncommon",
    label: "Uncommon",
    stars: 2,
    rank: 3,
    blurb: "Ten to twenty-four sightings",
  },
  {
    rarity: "common",
    label: "Common",
    stars: 1,
    rank: 4,
    blurb: "A regular: twenty-five sightings or more",
  },
];

const BY_RARITY = new Map(RARITIES.map((info) => [info.rarity, info]));

export function rarityInfo(rarity: Rarity): RarityInfo {
  const info = BY_RARITY.get(rarity);
  if (!info) {
    const known = [...BY_RARITY.keys()].join(", ");
    throw new Error(`Unknown rarity "${String(rarity)}"; expected one of ${known}`);
  }
  return info;
}

/**
 * Rarity from encounters on record (docs/api.md): legendary = 1 (or a fresh discovery),
 * epic = 2–3, rare = 4–9, uncommon = 10–24, common = 25+.
 */
export function rarityFromEncounters(encounters: number): Rarity {
  if (!Number.isFinite(encounters) || encounters < 0) {
    throw new Error(`Encounter count must be a non-negative number, got ${encounters}`);
  }
  if (encounters <= 1) return "legendary";
  if (encounters <= 3) return "epic";
  if (encounters <= 9) return "rare";
  if (encounters <= 24) return "uncommon";
  return "common";
}

/** Comparator: rarest first. */
export function compareRarity(a: Rarity, b: Rarity): number {
  return rarityInfo(a).rank - rarityInfo(b).rank;
}
